import { useEffect, useState, type FormEvent } from "react";
import { changePassword } from "../api/auth-api";
import { useApiClient } from "../api/ApiClientContext";
import { ApiError, userFacingMessage } from "../api/errors";
import { useAuth } from "../auth/AuthContext";
import { useCollection } from "../collection/CollectionContext";
import { useCollectionView } from "../collection/CollectionViewContext";
import { profileCollectionStats } from "../collection/collection-progress";
import { APP_BUILD_ID, APP_VERSION } from "../app/version";
import { Button } from "../components/Button";
import { CollectionPortabilityPanel } from "../components/CollectionPortabilityPanel";
import { ErrorMessage } from "../components/ErrorMessage";
import { LoadingIndicator } from "../components/LoadingIndicator";
import { TextInput } from "../components/TextInput";
import { Link } from "react-router-dom";

function formatNumbersProgress(
  owned: number,
  total: number | null,
  conflict: boolean,
): string {
  if (conflict || total === null) {
    return String(owned);
  }
  return `${owned}/${total}`;
}

function yearProgressPercent(owned: number, total: number | null): number {
  if (total === null || total <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round((owned / total) * 100)));
}

function formatYearRatio(owned: number, total: number | null): string {
  if (total === null) {
    return String(owned);
  }
  return `${owned}/${total}`;
}

export function ProfilePage() {
  const auth = useAuth();
  const client = useApiClient();
  const collection = useCollection();
  const view = useCollectionView();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  useEffect(() => {
    void view.ensureProgressMeta();
  }, [view.ensureProgressMeta]);

  if (auth.status !== "authenticated") {
    return null;
  }

  const account = auth.account;
  const stats =
    collection.status === "ready"
      ? profileCollectionStats(collection.items, view.progressMeta)
      : null;

  async function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);
    setPasswordBusy(true);
    try {
      await changePassword(client, currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setPasswordOpen(false);
      setPasswordSuccess("Пароль обновлён.");
    } catch (error) {
      if (error instanceof ApiError && error.code === "INVALID_CREDENTIALS") {
        setPasswordError("Неверный текущий пароль.");
      } else if (
        error instanceof ApiError &&
        error.code === "INVALID_REQUEST" &&
        error.message.trim() !== ""
      ) {
        setPasswordError(error.message);
      } else {
        setPasswordError(userFacingMessage(error));
      }
    } finally {
      setPasswordBusy(false);
    }
  }

  return (
    <section className="hw-page hw-page--profile">
      <h1 className="hw-page-title">Профиль</h1>

      <section className="hw-profile-section" aria-labelledby="profile-stats-heading">
        <h2 id="profile-stats-heading" className="hw-profile-section__title">
          Статистика
        </h2>
        {collection.status === "loading" || collection.status === "idle" ? (
          <LoadingIndicator label="Загрузка коллекции…" />
        ) : null}
        {collection.status === "error" ? (
          <ErrorMessage>
            {collection.errorMessage ?? "Не удалось загрузить коллекцию."}
          </ErrorMessage>
        ) : null}
        {stats ? (
          <>
            <dl className="hw-profile-stats hw-profile-stats--tiles">
              <div className="hw-profile-stat-tile">
                <dt>Всего экземпляров</dt>
                <dd>{stats.totalQuantity}</dd>
              </div>
              <div className="hw-profile-stat-tile">
                <dt>Уникальных вариантов</dt>
                <dd>{stats.uniqueCount}</dd>
              </div>
              <div className="hw-profile-stat-tile">
                <dt>Номера коллекционера</dt>
                <dd>
                  {formatNumbersProgress(
                    stats.numbersOwned,
                    stats.numbersTotal,
                    stats.numbersConflict,
                  )}
                </dd>
              </div>
            </dl>
            <dl className="hw-profile-rarity" aria-label="Редкость">
              <div>
                <dt>TH</dt>
                <dd>{stats.thCount}</dd>
              </div>
              <div>
                <dt>STH</dt>
                <dd>{stats.sthCount}</dd>
              </div>
              <div>
                <dt>Exclusive</dt>
                <dd>{stats.exclusiveCount}</dd>
              </div>
            </dl>
          </>
        ) : null}
      </section>

      {stats && stats.years.length > 0 ? (
        <section
          className="hw-profile-section"
          aria-labelledby="profile-years-heading"
        >
          <h2 id="profile-years-heading" className="hw-profile-section__title">
            Прогресс по годам
          </h2>
          <ul className="hw-profile-years" aria-label="По годам">
            {stats.years.map((year) => {
              const ratio = formatYearRatio(year.owned, year.total);
              const percent = yearProgressPercent(year.owned, year.total);
              return (
                <li key={year.value} className="hw-profile-year">
                  <span className="hw-profile-year__label">{year.label}</span>
                  <span
                    className="hw-profile-year__track"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={year.total ?? undefined}
                    aria-valuenow={year.owned}
                    aria-label={`${year.label}: ${year.description}`}
                  >
                    <span
                      className="hw-profile-year__fill"
                      style={{ width: `${percent}%` }}
                    />
                  </span>
                  <span className="hw-profile-year__ratio">{ratio}</span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <section className="hw-profile-section hw-profile-section--history">
        <Link className="hw-profile-history-link" to="/collection/history"><span><strong>История коллекции</strong><small>Изменения и последние действия</small></span><span className="hw-profile-history-link__chevron" aria-hidden="true">›</span></Link>
      </section>

      <CollectionPortabilityPanel />

      <section className="hw-profile-section hw-profile-section--security" aria-labelledby="profile-account-heading">
        <h2 id="profile-account-heading" className="hw-profile-section__title">
          Аккаунт
        </h2>
        <dl className="hw-profile-dl">
          <div>
            <dt>Имя</dt>
            <dd>{account.displayName}</dd>
          </div>
          <div>
            <dt>Логин</dt>
            <dd>{account.username}</dd>
          </div>
          {account.isDemo ? (
            <div>
              <dt>Режим</dt>
              <dd>
                Демо
                {account.demoExpiresAt
                  ? ` · до ${new Date(account.demoExpiresAt).toLocaleString("ru-RU")}`
                  : ""}
              </dd>
            </div>
          ) : null}
        </dl>

        <div className="hw-profile-actions">
          {!account.isDemo ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setPasswordOpen((value) => !value);
                setPasswordError(null);
                setPasswordSuccess(null);
              }}
            >
              Сменить пароль
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            className="hw-profile-logout"
            onClick={() => {
              void auth.logout();
            }}
          >
            Выйти
          </Button>
        </div>

        {passwordSuccess ? (
          <p className="hw-profile-success" role="status">
            {passwordSuccess}
          </p>
        ) : null}

        {passwordOpen && !account.isDemo ? (
          <form className="hw-profile-password" onSubmit={handlePasswordSubmit}>
            <TextInput
              id="profile-current-password"
              label="Текущий пароль"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
            />
            <TextInput
              id="profile-new-password"
              label="Новый пароль"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              minLength={15}
              maxLength={128}
            />
            <p className="hw-profile-note">Не меньше 15 символов.</p>
            {passwordError ? <ErrorMessage>{passwordError}</ErrorMessage> : null}
            <div className="hw-profile-actions">
              <Button
                type="submit"
                disabled={
                  passwordBusy || !currentPassword || newPassword.length < 15
                }
              >
                Сохранить пароль
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={passwordBusy}
                onClick={() => {
                  setPasswordOpen(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setPasswordError(null);
                }}
              >
                Отмена
              </Button>
            </div>
          </form>
        ) : null}
      </section>

      <section
        className="hw-profile-about"
        aria-labelledby="profile-about-heading"
      >
        <h2 id="profile-about-heading" className="hw-profile-about__title">
          О приложении
        </h2>
        <p className="hw-profile-about__meta">
          <span>Версия {APP_VERSION}</span>
          <span aria-hidden="true"> · </span>
          <span>Сборка {APP_BUILD_ID}</span>
        </p>
      </section>
    </section>
  );
}
